import { useCallback, useEffect, useMemo, useState } from "react";
import { getClientLimitStatusAsync } from "../Api/getClientLimitStatusAsync";
import { getUserFromLS } from "../utils/getUserFromLS";
import {
  applyCapabilityClosures,
  canUseAccountCalendar,
  hasCapability,
  hotlistSourceEnabled,
  CapabilityKey,
} from "../utils/capabilities";

interface UseClientCapabilitiesResult {
  capabilities: Record<string, boolean> | null;
  loading: boolean;
  /** Admin/internal users without a client_id — treat as no product gating (show everything). */
  isAdminWithoutClient: boolean;
  hasCapability: (key: CapabilityKey | string) => boolean;
  canUseAccountCalendar: boolean;
  hotlistSourceEnabled: (source: "accounts" | "industries" | "assets") => boolean;
}

/**
 * Loads module capabilities for the current user's client. Users without a
 * client_id (e.g. platform admins) are treated as ungated — every capability
 * check resolves to true so admin-only surfaces stay visible.
 */
export function useClientCapabilities(): UseClientCapabilitiesResult {
  const [capabilities, setCapabilities] = useState<Record<string, boolean> | null>(null);
  const [loading, setLoading] = useState(true);

  const user = getUserFromLS();
  const clientId = user.client_id;

  useEffect(() => {
    if (!clientId) {
      setCapabilities(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    getClientLimitStatusAsync(clientId)
      .then((status) => {
        if (cancelled) return;
        setCapabilities(applyCapabilityClosures(status.capabilities ?? {}));
      })
      .catch(() => {
        if (!cancelled) setCapabilities(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const isAdminWithoutClient = !clientId;

  const hasCapabilityFn = useCallback(
    (key: CapabilityKey | string) => (isAdminWithoutClient ? true : hasCapability(capabilities, key)),
    [capabilities, isAdminWithoutClient]
  );

  const hotlistSourceEnabledFn = useCallback(
    (source: "accounts" | "industries" | "assets") =>
      isAdminWithoutClient ? true : hotlistSourceEnabled(capabilities, source),
    [capabilities, isAdminWithoutClient]
  );

  return useMemo(
    () => ({
      capabilities,
      loading,
      isAdminWithoutClient,
      hasCapability: hasCapabilityFn,
      canUseAccountCalendar: isAdminWithoutClient ? true : canUseAccountCalendar(capabilities),
      hotlistSourceEnabled: hotlistSourceEnabledFn,
    }),
    [capabilities, loading, isAdminWithoutClient, hasCapabilityFn, hotlistSourceEnabledFn]
  );
}
